// Legal / compliance pages (/terms, /privacy, /refund, /contact). Standalone,
// Shell-free, in the landing's .lp aesthetic. English content (the authoritative
// legal version + what payment-gateway reviewers read). Content lives in
// lib/legal-content.ts; each route passes its `doc` slug.

import { useEffect, type ReactElement } from 'react';
import { master_app_url } from '../lib/marketing.js';
import { LEGAL_DOCS, LEGAL_ORDER, type LegalBlock } from '../lib/legal-content.js';
import './landing.css';

function BrandMark({ size = 32 }: { size?: number }): ReactElement {
  return (
    <span
      className="inline-grid place-items-center rounded-[0.4em] bg-primary text-white font-tamil leading-none shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.5 }}
      aria-hidden
    >
      சீ
    </span>
  );
}

function Block({ block }: { block: LegalBlock }): ReactElement {
  if (block.kind === 'h') {
    return <h2 className="lp-display text-xl text-primary-ink mt-9 mb-1" style={{ fontWeight: 600 }}>{block.text}</h2>;
  }
  if (block.kind === 'list') {
    return (
      <ul className="space-y-2 my-1">
        {block.items.map((it, i) => (
          <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-ink-2">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="text-[15px] leading-relaxed text-ink-2">{block.text}</p>;
}

export function LegalPage({ doc }: { doc: string }): ReactElement {
  const d = LEGAL_DOCS[doc] ?? LEGAL_DOCS.terms!;
  const signin_url = master_app_url('/login');

  useEffect(() => {
    document.title = `${d.title} — ChitDesk`;
    window.scrollTo({ top: 0 });
  }, [d.slug, d.title]);

  return (
    <main className="lp lp-canvas lp-grain min-h-screen text-ink antialiased overflow-x-hidden">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-40 border-b border-line/70" style={{ background: 'rgba(246,242,236,0.72)', backdropFilter: 'saturate(140%) blur(10px)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 sm:px-6 h-16">
          <a href="/" className="flex items-center gap-2.5 no-underline">
            <BrandMark size={34} />
            <span className="text-lg font-semibold tracking-tight text-primary-ink">ChitDesk</span>
          </a>
          <nav className="flex items-center gap-1 sm:gap-2">
            <a href="/" className="hidden sm:inline-block px-3.5 py-2 rounded-lg text-sm text-ink-2 hover:text-primary hover:bg-bg-elev transition no-underline">Home</a>
            <a href="/#pricing" className="hidden sm:inline-block px-3.5 py-2 rounded-lg text-sm text-ink-2 hover:text-primary hover:bg-bg-elev transition no-underline">Pricing</a>
            <a href={signin_url} className="lp-cta px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white no-underline">Open app</a>
          </nav>
        </div>
      </header>

      {/* ── Document ── */}
      <article className="max-w-3xl mx-auto px-5 sm:px-6 pt-12 pb-20">
        <h1 className="lp-display text-3xl sm:text-4xl text-primary-ink" style={{ fontWeight: 600 }}>{d.title}</h1>
        <p className="mt-2 text-ink-2">{d.tagline}</p>
        <p className="mt-1 text-sm text-ink-3">Last updated: {d.updated}</p>

        <div className="mt-8 space-y-3">
          {d.body.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </div>

        {/* Cross-links */}
        <div className="mt-12 pt-6 border-t border-line flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {LEGAL_ORDER.map((l) => (
            <a
              key={l.slug}
              href={`/${l.slug}`}
              className={`no-underline transition ${l.slug === d.slug ? 'text-primary font-medium' : 'text-ink-2 hover:text-primary'}`}
            >
              {l.label}
            </a>
          ))}
        </div>
      </article>

      {/* ── Footer ── */}
      <footer className="border-t border-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8">
          <p className="text-xs text-ink-3 max-w-2xl">
            ChitDesk is software for managing your business records. ChitDesk does not operate a chit fund and does not collect, pool or hold member deposits.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <BrandMark size={26} />
              <span className="text-sm font-semibold tracking-tight text-primary-ink">ChitDesk</span>
            </div>
            <div className="flex flex-wrap items-center gap-5 text-sm text-ink-2">
              <a href="/" className="hover:text-primary transition no-underline">Home</a>
              {LEGAL_ORDER.map((l) => (
                <a key={l.slug} href={`/${l.slug}`} className="hover:text-primary transition no-underline">{l.label}</a>
              ))}
            </div>
            <span className="text-xs text-ink-3">© {new Date().getFullYear()} ChitDesk</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
